'use client';

export const dynamic = 'force-dynamic';

import React, {
	useEffect,
	useRef,
	useState,
	useCallback,
	useMemo,
	Suspense,
} from 'react';
import { useSearchParams } from 'next/navigation';
import MessageCard from '@/components/Home/MessageCard';
import axios from 'axios';
import Footer from '@/components/layout/Footer';
import { BuilderComponent, builder } from '@builder.io/react';
import { columns } from '@/components/@generalComponents/InspirationMenu';
import PaywallCard from '@/components/@generalComponents/PaywallCard';
import MessagesSlider from '@/components/@generalComponents/MessagesSlider';
import SecondaryFooter from '@/components/layout/SecondaryFooter';
import LeftSidebar from '@/components/layout/LeftSidebar';
import SortControl from '@/components/@generalComponents/SortControl';
import TagFilter, {
	extractUniqueTags,
	filterByTag,
} from '@/components/@generalComponents/TagFilter';
import {
	withEffectiveTags,
	FILTER_TAG_LABELS,
	FILTER_TAG_ORDER,
	INSPIRATION_SLUG_TAGS,
	AUTO_TAG_RCS,
} from '@/utils/messageFilters';
import { fetchAvailableTags } from '@/utils/tagFilterApi';
import { applyPaywall, shouldShowPaywall } from '@/utils/paywall';
import { sortMessagesByDate } from '@/utils/sort';
import { UserAuth } from '@/app/context/AuthContext';
import { getAuthHeaders } from '@/utils/authHeader';
import { useSelector } from 'react-redux';
import { Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { SlidersHorizontal } from 'lucide-react';
import styles from './SearchPage.module.css';

// Owner request (Search only): tag chips limited to All + RCS. "All" still lists
// every result; fetching is unchanged (the inspiration-tag pass is not a chip).
const SEARCH_TAGS = [AUTO_TAG_RCS];
const onlySearchTags = (tags) =>
	Array.isArray(tags) ? tags.filter((t) => SEARCH_TAGS.includes(t)) : tags;

builder.init('0de1f0bc4e504a15a1cd1b48dbd897e2');
const BUILDER_API_KEY = '0de1f0bc4e504a15a1cd1b48dbd897e2';
const BUILDER_MODEL = 'search-page';
const BUILDER_BASE_URL = `https://cdn.builder.io/api/v2/content/${BUILDER_MODEL}`;

const priority_id = {
	'cart': [
		'6660e730aada11a6fba0ca40',
		'6692b172620de2bf0ef9d49d',
		'6635141d0db3848437de172d',
		'66478c77574172fc958270e8',
		'67ffb5d67be73275fc178a83',
		'67c4a84e524101d2118c1832',
		'661566521294807d0791823e',
		'680e5707f010361b8ce3c1a9',
		'67fe56002e779037001ef966',
		'661ef23282e06a7f665223f2',
		'65e0c452b1bdce8e0a674e6a',
		'65599e658a3a7472cad8451e',
		'65ca528bcb3cc95483c68915',
		'66671949b4dec816944fc1cc',
		'680e57fef010361b8ce3c1aa',
		'657891aa84f092ad523d47b4',
		'655a41fa8891ca24236cf8d7',
		'67ec1d31bc9116096b165fc3',
	],
	'back in stock': [
		'6787ea1bd4a6a776c95a7b25',
		'6781bb3db3f2a3450222700a',
		'6783db6c0b61deac25ae677a',
		'67813757024e8cc723b636fa',
		'6758d6ac3da156b5a73c9eaa',
		'684311a8775a4fecbf168cae',
		'68486b2bb22351ee6f442e9b',
		'684b176a960d38b2687f0611',
		'67880602d4a6a776c95a7b3b',
		'6844c5d7f78e533a65fb332c',
		'6782b82313b6718d1bea079c',
		'6782a3fa13b6718d1bea0783',
		'66f0877def5c3804b593b1ae',
	],
	'browse': [
		'666b38b5500155c4173d189e',
		'67854c2a951a47ffdaa68bb4',
		'664e5fb2122b5a547bb509bf',
		'67854d67951a47ffdaa68bb5',
		'6559b0a88891ca24236cf8be',
		'68404d8399e273338c5b3a23',
		'663bc9fb937f96b763e192cd',
		'65777cc7f3baf7e8da774edb',
		'67362d355ae4dee9265c8af1',
		'6841484597bac8ab599b7c88',
	],
	'funniest': [
		'6831f4175729794846b2b589',
		'66d9e4a2293d6bd858bc4db0',
		'684adf65960d38b2687f05de',
		'66f05bdbef5c3804b593b192',
		'68433b26775a4fecbf168ce1',
		'668c698a0691eae6a18b978f',
		'674be29c6da165284044e024',
		'6657be883645399e9f9b0bcf',
		'67156f7ff100ac17784a59b3',
		'6749cf312816f45443a09b6e',
		'6565698a7ecb8a018d93ec50',
		'67a6a438533402cabd8e0367',
		'6590459e8c757165fd094fea',
		'67ab6a13fedee990f57c6780',
		'68470ec031c58dc32097547d',
		'661f0f0e831d4e14fb39465d',
		'67a925f7252a77206af79016',
		'682cfb99c99e9dcb06993769',
		'6740d5701067c533ff05cd3d',
		'663e6d4db40772389ffcfeb7',
		'660ef7f3b0a92f2ae3594c07',
		'6818c4a98d551434bde2e9eb',
		'6841c61197bac8ab599b7caf',
	],
	'creative': [
		'66312981957c236847145b6a',
		'65c259297418a58a26f7baf8',
		'67dd63bce7ac0032423bf0d3',
		'655d440302b7b73eba0f149d',
		'66f2e2706537e93ae5bb694d',
		'6738d9f08378f9fd4f90155b',
		'66c9179803a5e91bfde54099',
		'655f78c01a889b0755f10cc4',
		'656e5e91437335fd1e5a9600',
		'67030bbef9e69bf98596d676',
		'65ca3142cb3cc95483c688f7',
		'6811290183ade2c96fb7beea',
		'67ffa55b7be73275fc178a77',
		'674be2ad6da165284044e025',
		'6842ca63775a4fecbf168c90',
		'681cdcb6515dd5920d8fa3b4',
		'674c8a3e57b597c9cd066294',
		'667ae32a8b5dbc8725b11302',
		'65de1fa2d9b4aa879f087a5c',
		'67ec004cbc9116096b165f9c',
	],
	'educational': [
		'6841ff4797bac8ab599b7cd9',
		'677fe427b7ff49464ccbd944',
		'6620202d831d4e14fb394681',
		'68474e7b31c58dc3209754a3',
		'67a6885d533402cabd8e0359',
		'684201f797bac8ab599b7cda',
		'66bb8e6a002677cfb654b769',
		'67f02cff69d175dadb9426f3',
		'68488103b22351ee6f442eaa',
		'660ff634b0a92f2ae3594c13',
		'67a3c6d94ad70babda448d31',
		'68376eee0cff953ba5b765cd',
		'684887d5b22351ee6f442eb1',
		'67bca62952d7421980f9376b',
	],
	'reviews': [
		'6786b5c56a4a5bf1aff0ee8c',
		'656652b04f978f934116e46a',
		'6578955184f092ad523d47b8',
		'6581d73385a92bb235e90d26',
		'6786a73e6a4a5bf1aff0ee7a',
		'65676e408aaf8a02d6840ed9',
		'67a669e1499375815c10f53c',
		'67e70e2559766b85de2a6deb',
		'656bacc051062adcbf244314',
		'657b6339e9daacacf3b09e8f',
	],
	'welcome': [
		'67ae52e905aefcd9a74a5b8e',
		'679f0bae78832af11827fb1d',
		'68065519f06c03043790de7b',
		'67ffbdae7be73275fc178a88',
		'67aa5e67252a77206af79077',
		'67f84d9e0bd37a4242388eaa',
		'65636c520a7b9a7436e32505',
		'679f0bac78832af11827fb1c',
		'65634e6c0a7b9a7436e324dc',
		'67f831600bd37a4242388e96',
		'65634e860a7b9a7436e324dd',
		'6749c78b2816f45443a09b4b',
		'67bcaf4052d7421980f93775',
		'672e79e43a2ed80f351a01fc',
		'67253f9197a67f2f9e233bd7',
		'6723ede64cf4f9a21911c895',
		'65588fd18a3a7472cad844ed',
		'68010f518c05953033db9568',
		'6726910f0e779f672ec4eee0',
		'6749c7632816f45443a09b49',
		'65634b680a7b9a7436e324af',
		'672933e923295a21ba285aec',
		'67f9832a9519e54a16373d6b',
		'67fea2d92e779037001ef9ef',
		'6803b2302754afdc95aa9913',
		'67ff2e742e779037001efa21',
	]
};

// Merges message lists into one, keeping each messageId only once. Lists
// earlier in `lists` win on duplicates.
const dedupeMessagesById = (lists) => {
	const seen = new Set();
	const result = [];
	for (const list of lists) {
		for (const message of list) {
			const id = message?.messageId;
			if (seen.has(id)) continue;
			seen.add(id);
			result.push(message);
		}
	}
	return result;
};

// Move utility function outside component to prevent re-creation
const getCategoryInfoFromSlug = (query) => {
	const flatList = columns.flat();

	for (const item of flatList) {
		const slugs = item.slug.split(',').map(s => s.trim().toLowerCase());
		if (slugs.includes(query.toLowerCase())) {
			return {
				name: item.name,
				description: item.shortDescription || item.shotDescription || '',
				slug: item.slug
			};
		}
	}

	return null;
};

const normalizeText = (text = '') => {
	let decoded = text;
	try {
		decoded = decodeURIComponent(text);
	} catch (e) {
		// noop: already decoded or invalid sequence
	}

	return decoded
		.toString()
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '') // strip accents
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
};

const isLooseMatch = (source, target) => {
	const normalizedSource = normalizeText(source);
	const normalizedTarget = normalizeText(target);

	if (!normalizedSource || !normalizedTarget) return false;

	return (
		normalizedSource === normalizedTarget ||
		normalizedSource.includes(normalizedTarget) ||
		normalizedTarget.includes(normalizedSource)
	);
};

const fetchBuilderEntries = async (queryString = '') => {
	const url = `${BUILDER_BASE_URL}?apiKey=${BUILDER_API_KEY}${queryString}`;
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Builder fetch failed: ${response.status}`);
	}
	return response.json();
};

const fetchAllBuilderEntries = async (limit = 50, maxPages = 5) => {
	const allResults = [];

	for (let page = 0; page < maxPages; page++) {
		const offset = page * limit;
		const data = await fetchBuilderEntries(`&limit=${limit}&offset=${offset}`);
		const results = data?.results || [];
		allResults.push(...results);

		if (results.length < limit) break;
	}

	return allResults;
};

const SearchPageContent = () => {
	const searchParams = useSearchParams();
	const searchQuery = searchParams.get('query') || '';
	const { user, isPro } = UserAuth();
	const allCategories = useSelector((s) => s.categoryReducer.allCategories);
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));
	const [sortDirection, setSortDirection] = useState('desc');
	const [selectedCategoryId, setSelectedCategoryId] = useState('');
	const [selectedTag, setSelectedTag] = useState('');
	const [filterAnchor, setFilterAnchor] = useState(null);
	const selectedCategoryName = allCategories?.find(
		(c) => c.categoryId === selectedCategoryId
	)?.name;

	// Chips normally come from the server's available-tags endpoint, which
	// covers this search's ENTIRE result set (not just what's loaded on
	// screen). `tagsSource` starts as 'client' so a chip list is available
	// immediately from whatever messages are already loaded, then upgrades to
	// 'server' once the available-tags call resolves. If that call - or a
	// tag-filtered results fetch below - ever fails (e.g. the backend hasn't
	// been deployed with this support yet), we fall back to 'client' and
	// behave exactly as this page did before server-side tag filtering
	// existed: chips derived from loaded messages, filtering done in memory.
	// This keeps the page working even when frontend and backend are a
	// release apart. Note: the server's available-tags/tag support does not
	// know about this page's client-side category filter (selectedCategoryId)
	// - that filter is applied on top of the server's (unfiltered-by-category)
	// results, exactly as it already was applied on top of loaded messages
	// before this change.
	const [tagsSource, setTagsSource] = useState('client');
	const [serverTags, setServerTags] = useState([]);
	const [filteredMessages, setFilteredMessages] = useState([]);
	const [filteredPagination, setFilteredPagination] = useState(null);
	const [filteredLoading, setFilteredLoading] = useState(false);

	// Guards against out-of-order responses when the search query changes
	// quickly.
	const tagsRequestIdRef = useRef(0);
	// Guards against out-of-order responses when the selected tag (or a
	// "Load More" click while filtering) changes/fires quickly in succession.
	const filterRequestIdRef = useRef(0);
	// Guards against out-of-order responses when fetchMessages itself is
	// called again (e.g. searchQuery changes) before its earlier call's
	// best-effort tag-fetch has resolved.
	const fetchRequestIdRef = useRef(0);

	const isFiltering = Boolean(selectedTag) && tagsSource === 'server';

	// Consolidated state
	const [state, setState] = useState({
		page: 0,
		messages: [],
		pagination: null,
		isNextDisabled: false,
		builderContent: null,
		isLoading: false,
		error: null
	});
	const size = 10; // Made constant since it never changes

	// Memoize category info to prevent unnecessary recalculations
	const categoryInfo = useMemo(() => {
		return getCategoryInfoFromSlug(searchQuery);
	}, [searchQuery]);

	// Memoize priority IDs string
	const priorityIdsString = useMemo(() => {
		const priorityIdsArray = priority_id[searchQuery] || [];
		return priorityIdsArray.join(',');
	}, [searchQuery]);

	// Fetch builder content function
	const fetchBuilderContent = useCallback(async (query) => {
		const normalizedQuery = query?.trim();

		if (!normalizedQuery) {
			setState(prev => ({ ...prev, builderContent: null }));
			return;
		}

		try {
			// First try: query Builder by the custom field `data.searchQuery` (fast/accurate)
			const targetedData = await fetchBuilderEntries(
				`&limit=25&query[data.searchQuery]=${encodeURIComponent(normalizedQuery.toLowerCase())}`
			);

			let results = targetedData?.results || [];
			// Helper: generate variants for matching (decoded, hyphen, plus, nospace)
			const makeVariants = (s) => {
				const raw = (s || '').toString();
				let decoded = raw;
				try { decoded = decodeURIComponent(raw); } catch (e) { /* ignore */ }
				const base = decoded.toLowerCase().trim();
				const hyphen = base.replace(/\s+/g, '-');
				const plus = base.replace(/\s+/g, '+');
				const nospace = base.replace(/\s+/g, '');
				return new Set([base, hyphen, plus, nospace]);
			};

			const queryVariants = makeVariants(normalizedQuery);
			const matchesEntry = (item) => {
				const itemQuery = (item?.data?.searchQuery || '').toString();
				const title = (item?.data?.title || '').toString().toLowerCase();
				const itemVariants = makeVariants(itemQuery);

				for (const v of queryVariants) {
					if (itemVariants.has(v)) return true;
				}

				for (const v of queryVariants) {
					if (v && title.includes(v)) return true;
				}

				return (
					isLooseMatch(normalizedQuery, itemQuery) ||
					isLooseMatch(normalizedQuery, title)
				);
			};

			// Try to find a best match among results (match by data.searchQuery or title)
			let matched = results.filter(matchesEntry);

			// Fallback: if nothing matched, fetch all `search-page` entries and try matching by title or data.searchQuery.
			if (!matched || matched.length === 0) {
				results = await fetchAllBuilderEntries(50, 6);
				// reuse the same matching logic for the fallback
				matched = results.filter(matchesEntry);
			}

			setState(prev => ({
				...prev,
				builderContent: matched && matched.length > 0 ? matched[0] : null
			}));
		} catch (err) {
			console.error('Builder content fetch error:', err);
			setState(prev => ({
				...prev,
				builderContent: null,
				error: 'Failed to fetch builder content'
			}));
		}
	}, []);

	// Fetch messages function
	const fetchMessages = useCallback(async (pageNumber = 0, isLoadMore = false) => {
		if (!searchQuery) return;

		const myFetchRequestId = ++fetchRequestIdRef.current;

		setState(prev => ({ ...prev, isLoading: true, error: null }));

		try {
			const response = await axios.get('https://api.tinytexts.co/api/messages/search', {
				params: {
					page: pageNumber,
					size,
					query: searchQuery,
					priorityIds: priorityIdsString,
				},
				headers: await getAuthHeaders(),
			});

			let { messages: fetchedMessages, pagination: fetchedPagination } = response.data;

			if (searchQuery.toLowerCase() === 'welcome') {
				fetchedMessages = [...fetchedMessages].sort((a, b) =>
					(a.brand?.brandId || '').localeCompare(b.brand?.brandId || '')
				);
			}

			setState(prev => ({
				...prev,
				messages: isLoadMore ? [...prev.messages, ...fetchedMessages] : fetchedMessages,
				pagination: fetchedPagination || null,
				isNextDisabled: !fetchedPagination?.nextPage,
				isLoading: false,
				page: pageNumber
			}));

			// Additive, best-effort pass: for a handful of Inspiration categories
			// that map to a manual tag, pull in any messages carrying that tag
			// and append the ones not already present, on top of the existing
			// hand-picked results. Only runs on the initial (page 0) load so
			// "Load More" pagination isn't affected. Never throws or blocks the
			// main result - if the backend doesn't support `tag` yet (or the
			// request otherwise fails), we just skip this silently. Kicked off
			// after the base results are already shown so this extra request
			// doesn't delay the main results appearing on screen.
			if (!isLoadMore) {
				const inspirationTag = INSPIRATION_SLUG_TAGS[searchQuery];
				if (inspirationTag) {
					try {
						const tagResponse = await axios.get('https://api.tinytexts.co/api/messages/search', {
							params: {
								page: 0,
								size,
								query: searchQuery,
								tag: inspirationTag,
							},
							headers: await getAuthHeaders(),
						});

						if (fetchRequestIdRef.current !== myFetchRequestId) return; // stale response

						const tagMessages = tagResponse.data?.messages || [];

						setState(prev => {
							const existingIds = new Set(prev.messages.map((m) => m.messageId));
							const extraMessages = tagMessages.filter((m) => !existingIds.has(m.messageId));
							if (extraMessages.length === 0) return prev;
							return { ...prev, messages: [...prev.messages, ...extraMessages] };
						});
					} catch (tagError) {
						console.error('Failed to fetch tag-matched inspiration messages:', tagError);
					}
				}
			}
		} catch (error) {
			console.error("Fetch messages error:", error);
			setState(prev => ({
				...prev,
				messages: isLoadMore ? prev.messages : [],
				isLoading: false,
			}));
		}
	}, [searchQuery, priorityIdsString, size]);

	// Fetches a page of results carrying `tag` directly from the server
	// (across this search's whole result set - same query/priorityIds scope
	// as fetchMessages above), replacing (page 0) or appending to (subsequent
	// pages) the filtered list.
	const fetchFilteredMessages = useCallback(async (pageNumber, tag, isLoadMore) => {
		const myRequestId = ++filterRequestIdRef.current;
		setFilteredLoading(true);
		try {
			const response = await axios.get('https://api.tinytexts.co/api/messages/search', {
				params: {
					page: pageNumber,
					size,
					query: searchQuery,
					priorityIds: priorityIdsString,
					tag,
				},
				headers: await getAuthHeaders(),
			});
			if (filterRequestIdRef.current !== myRequestId) return; // stale response

			const { messages: fetchedMessages, pagination: fetchedPagination } = response.data;
			setFilteredMessages((prev) =>
				isLoadMore ? [...prev, ...(fetchedMessages || [])] : fetchedMessages || []
			);
			setFilteredPagination(fetchedPagination || null);
			setFilteredLoading(false);
		} catch (error) {
			if (filterRequestIdRef.current !== myRequestId) return; // stale response
			console.error('Failed to fetch tag-filtered search results:', error);
			// Resilience fallback: revert to filtering the results already
			// loaded on the client, exactly as this page behaved before
			// server-side tag filtering existed, rather than showing an error
			// or a stuck loading state.
			setTagsSource('client');
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
		}
	}, [searchQuery, priorityIdsString, size]);

	// Reset state when search query changes
	useEffect(() => {
		setState(prev => ({
			...prev,
			page: 0,
			messages: [],
			pagination: null,
			isNextDisabled: false,
			error: null
		}));
		setSelectedTag('');
		setTagsSource('client');
		setServerTags([]);
		setFilteredMessages([]);
		setFilteredPagination(null);

		const myRequestId = ++tagsRequestIdRef.current;

		// Fetch initial data
		if (searchQuery) {
			fetchMessages(0, false);
			fetchBuilderContent(searchQuery);
			(async () => {
				const tags = await fetchAvailableTags({
					query: searchQuery,
					priorityIds: priorityIdsString,
				});
				if (tagsRequestIdRef.current !== myRequestId) return; // stale (query changed again)
				if (Array.isArray(tags)) {
					const shown = onlySearchTags(tags);
					setServerTags(shown);
					// A chip picked from the fallback list that the server doesn't
					// have here would keep filtering with no visible chip; clear it.
					setSelectedTag((prev) => (prev && !shown.includes(prev) ? '' : prev));
					setTagsSource('server');
				}
				// On failure (`tags === null`) we deliberately stay in 'client'
				// mode; see the resilience comment on `tagsSource` above.
			})();
		}
	}, [searchQuery, priorityIdsString, fetchMessages, fetchBuilderContent]);

	// Refetch when the signed-in user or PRO status changes so the backend
	// returns the right tier. The first run is skipped: the initial fetch
	// above already waits for the saved session and sends the token.
	const authKey = `${user?.uid ?? ''}:${isPro ? 'pro' : 'free'}`;
	const lastAuthKeyRef = useRef(authKey);
	useEffect(() => {
		if (lastAuthKeyRef.current === authKey) return;
		lastAuthKeyRef.current = authKey;
		if (!searchQuery) return;
		fetchMessages(0, false);
		if (selectedTag && tagsSource === 'server') {
			fetchFilteredMessages(0, selectedTag, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authKey]);

	// When the selected tag changes (and the server is known to support tag
	// filtering), fetch page 0 of the filtered results and replace the
	// filtered list. Clearing the tag just stops using `filteredMessages` -
	// no fetch is needed since `state.messages` already holds the unfiltered
	// list.
	useEffect(() => {
		if (!selectedTag || tagsSource !== 'server') {
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
			return;
		}
		// Drop the previous chip's results so they aren't merged into this
		// chip's list while its page 0 is loading.
		setFilteredMessages([]);
		setFilteredPagination(null);
		fetchFilteredMessages(0, selectedTag, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTag, tagsSource, searchQuery]);

	// Load more messages handler
	const loadMoreMessages = useCallback(() => {
		if (!state.isNextDisabled && !state.isLoading) {
			const nextPage = state.page + 1;
			fetchMessages(nextPage, true);
		}
	}, [state.isNextDisabled, state.isLoading, state.page, fetchMessages]);

	const isLoadMoreDisabled = isFiltering
		? filteredLoading || !filteredPagination?.nextPage
		: state.isNextDisabled || state.isLoading;

	const handleLoadMore = useCallback(() => {
		if (isFiltering) {
			if (!filteredLoading && filteredPagination?.nextPage) {
				fetchFilteredMessages(filteredPagination.nextPage, selectedTag, true);
			}
			return;
		}
		loadMoreMessages();
	}, [
		isFiltering,
		filteredLoading,
		filteredPagination,
		selectedTag,
		fetchFilteredMessages,
		loadMoreMessages,
	]);

	// Switching category starts unfiltered: the chosen tag may not exist in the
	// new category, which would leave its chip hidden while still filtering.
	useEffect(() => {
		setSelectedTag('');
	}, [selectedCategoryId]);

	// Paywall computation (used in both mobile and desktop branches).
	// The category filter is client-side only (the backend's tag support has
	// no category param for search) and applies on top of whichever message
	// list is currently active - the server-filtered list while a tag is
	// selected, otherwise the plain loaded list - before the tag filter,
	// sort, and paywall run, in that order.
	const categoryFilter = (list) =>
		selectedCategoryId
			? list.filter((m) => m?.brand?.category === selectedCategoryId)
			: list;
	const sourceMessages = categoryFilter(state.messages);
	const effectiveMessages = withEffectiveTags(sourceMessages);
	// Fallback derives from effective tags so auto tags like RCS are included.
	const availableTags = onlySearchTags(
		tagsSource === 'server' ? serverTags : extractUniqueTags(effectiveMessages)
	);
	// While a chip is selected, union the server's tag-filtered results with
	// whatever already-loaded messages carry that tag. The additive
	// inspiration-tag pass in fetchMessages fetches extra messages by tag
	// alone, without priorityIds, so the server's tag+priorityIds query here
	// can't see them - without this union, cards visible under "All" would
	// disappear once their chip is selected. Server entries win on
	// duplicate messageIds.
	const serverFilteredMessages = isFiltering
		? withEffectiveTags(categoryFilter(filteredMessages))
		: [];
	const clientTaggedMessages = filterByTag(effectiveMessages, selectedTag);
	const taggedMessages = isFiltering
		? dedupeMessagesById([serverFilteredMessages, clientTaggedMessages])
		: clientTaggedMessages;
	const sortedMessages = sortMessagesByDate(taggedMessages, sortDirection);
	const { visible } = applyPaywall(sortedMessages);
	const activePagination = isFiltering ? filteredPagination : state.pagination;
	const paywalled = shouldShowPaywall({
		user,
		isPro,
		count: visible.length,
		hasNextPage: Boolean(activePagination?.nextPage),
		hasOlder: activePagination?.hasOlder === true,
	});

	return (
		<>
		<div className='flex'>
			<LeftSidebar />
			<div className='flex-1 min-w-0 overflow-x-hidden px-4 mx-auto mt-3 mb-5'>
				{categoryInfo && (
					<div className="max-w-5xl px-4 mx-auto mt-6 mb-6">
						{/* Category Header */}
						<div className="mb-4 text-center">
							<h1 className="mb-2 text-4xl font-bold text-black">
								{categoryInfo.name}
							</h1>
							<p className="text-lg text-gray-700">
								{categoryInfo.description}
							</p>
						</div>
						
						
					</div>
				)}

				{state.error && (
					<div className="max-w-5xl px-4 mx-auto mt-6 mb-4 text-center">
						<p className="text-red-600">{state.error}</p>
					</div>
				)}

				<div className='flex flex-wrap items-center gap-4 mb-6'>
					<button
						type='button'
						onClick={(e) => setFilterAnchor(e.currentTarget)}
						className='flex items-center gap-1 text-sm text-gray-700 hover:text-black'
					>
						<span>
							Filter
							{selectedCategoryName && (
								<span className='font-medium'>: {selectedCategoryName}</span>
							)}
						</span>
						<SlidersHorizontal size={16} />
					</button>
					<Menu
						anchorEl={filterAnchor}
						open={Boolean(filterAnchor)}
						onClose={() => setFilterAnchor(null)}
					>
						<MenuItem
							selected={!selectedCategoryId}
							onClick={() => { setSelectedCategoryId(''); setFilterAnchor(null); }}
						>
							All categories
						</MenuItem>
						{allCategories?.map((cat) => (
							<MenuItem
								key={cat.categoryId}
								selected={selectedCategoryId === cat.categoryId}
								onClick={() => { setSelectedCategoryId(cat.categoryId); setFilterAnchor(null); }}
							>
								{cat.name}
							</MenuItem>
						))}
					</Menu>
					<SortControl value={sortDirection} onChange={setSortDirection} />
					<TagFilter
						tags={availableTags}
						selected={selectedTag}
						onSelect={setSelectedTag}
						labels={FILTER_TAG_LABELS}
						order={FILTER_TAG_ORDER}
						isPro={isPro}
					/>
				</div>

				{state.isLoading && sourceMessages.length === 0 ? (
					<div className='flex items-center justify-center h-64'>
						<div className='w-12 h-12 border-4 border-blue-700 rounded-full border-t-primary animate-spin' />
					</div>
				) : !state.isLoading && sourceMessages.length === 0 ? (
					<div className='flex items-center justify-center h-40 text-gray-500 text-sm'>
						No messages found{selectedCategoryId ? ' for this filter' : ''}.
					</div>
				) : isFiltering && filteredLoading && taggedMessages.length === 0 ? (
					<div className='flex items-center justify-center h-40 text-gray-500 text-sm'>
						Loading messages for this filter…
					</div>
				) : isFiltering && sortedMessages.length === 0 ? (
					<div className='flex items-center justify-center h-40 text-gray-500 text-sm'>
						No results match this filter.
					</div>
				) : !isFiltering && selectedTag && sortedMessages.length === 0 ? (
					<div className='flex items-center justify-center h-40 text-gray-500 text-sm'>
						No results loaded yet match this filter.
					</div>
				) : isMd ? (
					<MessagesSlider
						messages={visible}
						paywalled={paywalled}
						fetchMoreMessages={async () => {
							if (!paywalled && !isLoadMoreDisabled) {
								handleLoadMore();
							}
							return [];
						}}
					/>
				) : (
					<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
						{visible?.map((message) => (
							<MessageCard
								key={message?.messageId}
								message={message}
							/>
						))}
					</div>
				)}

				{!isMd && paywalled && <PaywallCard />}

				{!isMd && !paywalled && !selectedCategoryId && activePagination?.nextPage && (
					<div className='flex justify-center mt-6'>
						<button
							className={`px-4 py-2 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-300 ${
								isLoadMoreDisabled
									? 'bg-gray-400 cursor-not-allowed'
									: 'bg-blue-700 hover:bg-blue-600'
							}`}
							onClick={handleLoadMore}
							aria-label='Load more messages'
							disabled={isLoadMoreDisabled}
						>
							{(isFiltering ? filteredLoading : state.isLoading) ? 'Loading...' : 'Load More'}
						</button>
					</div>
				)}

				{state.builderContent && (
					<div className={styles.builderContent}>
						<BuilderComponent
							model="search-page"
							content={state.builderContent}
						/>
					</div>
				)}
				{/* <Footer /> */}
			</div>
		</div>
		<SecondaryFooter />
		</>
	);
};

const SearchPage = () => (
	<Suspense fallback={null}>
		<SearchPageContent />
	</Suspense>
);

export default SearchPage;
